import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Typography,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ReloadOutlined, FileAddOutlined } from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_MAIN_ENFORCE_LAWYER,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import UpdateEnforceDoc from "./modal/UpdateEnforceDoc";

const { Option } = Select;
const { Text } = Typography;

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD MMM YY") : "-";

const getCaseTypeMeta = (flagLoanNew, flagDue) => {
  if (flagLoanNew === 1) return { color: "blue", label: "ทำยอม" };
  if (flagLoanNew === 2 && flagDue === 1)
    return { color: "purple", label: "พิพากษามีดิว" };
  return { color: "default", label: "พิพากษาปกติ" };
};

const getStageColor = (currentStatus) => {
  if (currentStatus === 11) return "green";
  if (currentStatus === 9 || currentStatus === 10) return "orange";
  return "default";
};

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState();
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_MAIN_ENFORCE_LAWYER,
        { userId: roleId === 1 || roleId === 2 ? 1 : userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );
      console.log("res.data.data", res.data.data);

      if (res.data.data && res.data.data.length > 0) {
        const sortedData = [...res.data.data]
          .sort((a, b) => {
            const timeA = a.assignDate ? dayjs(a.assignDate).valueOf() : 0;
            const timeB = b.assignDate ? dayjs(b.assignDate).valueOf() : 0;
            return timeA - timeB;
          })
          .map((item, index) => ({
            ...item,
            key: item.CONTNO || item.id || `row-${index}`,
          }));
        setDataArr(sortedData);
      } else {
        setDataArr([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
      }
    } finally {
      setLoading(false);
    }

    return () => {
      controller.abort();
    };
  }, [userId]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select
  const statusOptions = useMemo(
    () => [
      { value: "99", icon: "🗂️", label: "ทั้งหมด" },
      { value: "9,10", icon: "🕒", label: "รอดําเนินการ" },
      { value: "11", icon: "📜", label: "วางคำบังคับแล้ว" },
    ],
    [],
  );

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      let passStatus = true;
      if (processStatus && processStatus !== "99") {
        const statusValues = processStatus.split(",").map(Number);
        passStatus = statusValues.includes(item.currentStatus);
      }

      return passQuery && passStatus;
    });
  }, [dataArr, searchInputTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus("99");
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  // ✅ Reload หลังบันทึกสำเร็จ
  const handleReload = useCallback(() => {
    loadMain();
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "newContno",
      key: "newContno",
      align: "center",
      width: 140,
      render: (value) => (
        <Text copyable strong>
          {value || "-"}
        </Text>
      ),
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "left",
      render: (_, record) => (
        <Text copyable strong>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </Text>
      ),
    },
    {
      title: "รายละเอียด",
      key: "redCaseNumber",
      align: "left",
      width: 220,
      render: (_, record) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <span style={{ color: "#888" }}>เลขคดีแดง </span>
            <Text copyable strong>
              {record.redCaseNumber || "-"}
            </Text>
          </div>
          <div>
            <span style={{ color: "#888" }}>ศาล </span>
            {record.provincialCourt || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>วันพิพากษา </span>
            {formatThaiDate(record.judgeDate)}
          </div>
        </div>
      ),
    },
    {
      title: "วันส่งงานทีมบังคับคดี",
      key: "assignDate",
      align: "left",
      width: 180,
      render: (_, record) =>
        record.assignDate ? (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div>
              <span style={{ color: "#888" }}>ผู้รับผิดชอบ </span>
              <Text strong>{record.receiveByName || "-"}</Text>
            </div>
            <div style={{ color: "#888" }}>
              {formatThaiDate(record.assignDate)}
            </div>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "สถานะเอกสาร",
      key: "docStatus",
      align: "left",
      width: 200,
      render: (_, record) => {
        const caseType = getCaseTypeMeta(record.flagLoanNew, record.flagDue);
        return (
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            <Tag color={caseType.color}>{caseType.label}</Tag>
            <div>
              <div>
                {record.flagLoanNew === 2 && (
                  <>
                    <Tag color={record.enforceCaseDate ? "green" : "default"}>
                      คำบังคับ
                    </Tag>
                    <span style={{ color: "#888" }}>
                      {formatThaiDate(record.enforceCaseDate)}
                    </span>
                  </>
                )}
              </div>
              <Tag color={record.docDate ? "green" : "default"}>หมายตั้ง</Tag>
              <span style={{ color: "#888" }}>
                {formatThaiDate(record.docDate)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "สถานะ",
      key: "stage",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Tag color={getStageColor(record.currentStatus)}>
          {record.stageText}
        </Tag>
      ),
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        return (
          <Button
            type="primary"
            ghost
            icon={<FileAddOutlined />}
            onClick={() => {
              setDataRecord(record);
              setIsModal(true);
            }}
          >
            เพิ่มไฟล์เอกสาร
          </Button>
        );
      },
    },
  ].filter(Boolean);

  return (
    <Card>
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Row gutter={[0, 16]}>
          {/* Filters */}
          <Col span={24}>
            <Space size={10} wrap>
              <Select
                size="large"
                style={{ minWidth: 180 }}
                placeholder="เลือกสถานะ"
                value={processStatus}
                onChange={setProcessStatus}
              >
                {statusOptions.map(({ value, icon, label }) => (
                  <Option key={value} value={value}>
                    <span style={{ marginRight: 8 }}>{icon}</span>
                    {label}
                  </Option>
                ))}
              </Select>

              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ"
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span style={{ marginRight: 5 }}>🔍</span>}
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                size="large"
                style={{
                  borderRadius: 8,
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffa39e",
                  color: "#cf1322",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </Space>
          </Col>

          {/* Table */}
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={arrayTable}
              rowKey="key"
              scroll={{ x: 1000 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              footer={() => (
                <div style={{ textAlign: "left", fontWeight: 500 }}>
                  จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                </div>
              )}
            />
          </Col>
        </Row>
      </Spin>

      {isModal && (
        <UpdateEnforceDoc
          open={isModal}
          onCancel={setIsModal}
          dataDefault={dataRecord}
          onSuccess={handleReload}
        />
      )}
    </Card>
  );
};

const MainEnforceDoc = MotionHoc(Main);
export default MainEnforceDoc;
